/**
 * Converts a hex color to HSL values
 */
const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  // Remove the hash if it exists
  hex = hex?.replace("#", "") ?? "";

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else if (max === b) {
      h = (r - g) / d + 4;
    }

    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converts HSL values to hex color
 */
const HSLToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };

  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * @param baseColor - The base color in hex format (e.g., "#FF0000" for red)
 * @param count - Number of color variations to generate
 * @returns Array of hex color codes from darker to lighter
 */
export const getColorVariations = (
  baseColor: string,
  count: number,
  step: number = 3, // 3 is the default step, difference between variations
): string[] => {
  const { h, s, l } = hexToHSL(baseColor);
  const colors: string[] = [];

  // Calculate the starting point to ensure we have equal steps above and below the base color
  const totalRange = step * (count - 1);
  const startLightness = l - totalRange / 2;

  // Generate colors from darker to lighter
  for (let i = 0; i < count; i++) {
    const newLightness = Math.max(0, Math.min(100, startLightness + step * i));
    colors.push(HSLToHex(h, s, newLightness));
  }

  return colors;
};
